import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fechaLarga, hhmm, soles } from '../../../compartido/formato';
import type { Fecha, Hora } from '../../../compartido/tipos';
import { Calendario, type EstadoDia } from '../componentes/Calendario';
import { GrillaHoras } from '../componentes/Horas';
import { Escudo, FlechaDer } from '../componentes/Iconos';
import { AvisoError, CabeceraMarca, CabeceraPaso, Cargando } from '../componentes/Piezas';
import { api, ErrorDeApi, mensajeDeError } from '../lib/api';
import { useConfig } from '../lib/config';
import { hoyEnLima, mesDe, sumarDias, sumarMeses } from '../lib/fechas';
import { leerLocal, recordarLocal, useDisponibilidad, useMedia } from '../lib/ganchos';
import { olvidarRetencion, recordarCorreo, recordarRetencion, retencionGuardada, type Retencion } from '../lib/sesion';

const CORREO_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CLAVE_CORREO = 'mentorking:ultimoCorreo';

/**
 * Pasos 1 y 2: el día y las horas. En celular son dos pantallas ("/" y
 * "/dia/:fecha"); en escritorio, el calendario y el panel de horas van lado
 * a lado y la URL sigue al día elegido, para poder recargar o compartir.
 */
export function Reservar() {
  const { fecha: fechaRuta } = useParams();
  const navegar = useNavigate();
  const escritorio = useMedia('(min-width: 960px)');
  const { config, error: errorConfig } = useConfig();

  const hoy = hoyEnLima();
  const [mes, setMes] = useState(() => mesDe(fechaRuta ?? hoy));
  const [elegido, setElegido] = useState<Fecha | undefined>(fechaRuta);
  // El mes a la vista y el del día elegido: pueden ser distintos.
  const disp = useDisponibilidad(mes, elegido && mesDe(elegido));

  useEffect(() => {
    setElegido(fechaRuta);
    if (fechaRuta) setMes(mesDe(fechaRuta));
  }, [fechaRuta]);

  // Volvió atrás desde el pago: se sueltan sus horas retenidas para que
  // pueda cambiarlas, en vez de verlas tachadas (retenidas por él mismo).
  // Acá y no en el panel de horas: en celular vuelve primero al calendario.
  const [previa] = useState(() => retencionGuardada());
  const [soltando, setSoltando] = useState(previa !== undefined);
  const recargar = disp.recargar;
  useEffect(() => {
    if (!previa) return;
    let vivo = true;
    api.soltar(previa.id).then(
      () => {
        olvidarRetencion();
        if (vivo) {
          setSoltando(false);
          recargar();
        }
      },
      (e) => {
        if (!vivo) return;
        setSoltando(false);
        if (e instanceof ErrorDeApi && (e.motivo === 'COBRO_EN_CURSO' || e.motivo === 'YA_PAGADA')) {
          // Ya empezó a pagar esas horas: no se tocan. Se lo lleva a su pago.
          navegar(`/pago/${previa.id}`, { replace: true });
        } else {
          olvidarRetencion();
          recargar();
        }
      },
    );
    return () => {
      vivo = false;
    };
    // Una sola vez al llegar, con la retención leída al montar.
  }, []);

  if (errorConfig) {
    return (
      <div className="pantalla">
        <CabeceraMarca />
        <div className="contenido">
          <AvisoError>{mensajeDeError(errorConfig)}</AvisoError>
        </div>
      </div>
    );
  }
  if (!config) return <Cargando />;

  const ultimo = sumarDias(hoy, config.ventanaDias);
  const estado = (f: Fecha): EstadoDia => {
    if (disp.libres(f).length > 0) return 'libre';
    const cerrado = config.diasCerrados.includes(new Date(`${f}T12:00:00Z`).getUTCDay());
    return !cerrado && f > hoy && f <= ultimo ? 'lleno' : 'no';
  };

  const elegir = (f: Fecha) => {
    setElegido(f);
    if (escritorio) navegar(`/dia/${f}`, { replace: true });
  };

  const calendario = (
    <div className="contenido">
      <div>
        <div className="antetitulo">Mentoría</div>
        <h1 className="titulo" style={{ marginTop: 8 }}>
          Traes un problema. Salimos con una decisión.
        </h1>
        <p className="parrafo" style={{ marginTop: 9 }}>
          Reservas las horas que necesites, de {config.horaApertura} de la mañana a {config.horaCierre - 12} de la noche.
        </p>
      </div>

      <div className="tarjeta" style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
        <span className="monto" style={{ fontSize: 21 }}>
          {soles(config.tarifaPorHora)}
        </span>
        <span className="parrafo" style={{ fontSize: 13 }}>
          la hora · sin paquetes ni mínimos
        </span>
      </div>

      {disp.error ? (
        <AvisoError>
          {mensajeDeError(disp.error)}{' '}
          <button type="button" className="enlace-texto" onClick={disp.recargar}>
            Reintentar
          </button>
        </AvisoError>
      ) : null}

      <Calendario
        mes={mes}
        estado={estado}
        elegido={elegido}
        hoy={hoy}
        onElegir={elegir}
        onMes={(d) => setMes((m) => sumarMeses(m, d))}
        puedeAtras={mes > mesDe(hoy)}
        puedeAdelante={mes < mesDe(ultimo)}
        cargando={!disp.tiene(mes) || soltando}
      />

      <p className="nota">
        Reservas hasta el {fechaLarga(ultimo).replace(/^\S+ /, '')}, con 24 horas de anticipación mínima. Puedes reprogramar hasta 2
        veces. No hay devoluciones.
      </p>
    </div>
  );

  // Celular, paso 1: solo el calendario, con el botón para seguir.
  if (!escritorio && !fechaRuta) {
    return (
      <div className="pantalla">
        <CabeceraMarca accion={{ a: '/acceso', texto: 'Mi sesión' }} />
        {calendario}
        <div className="pie">
          <button
            type="button"
            className="boton"
            disabled={!elegido || estado(elegido) !== 'libre'}
            onClick={() => elegido && navegar(`/dia/${elegido}`)}
          >
            {elegido ? `Continuar · ${fechaLarga(elegido).split(' ').slice(0, 2).join(' ')}` : 'Elige un día'}
            <FlechaDer color="currentColor" />
          </button>
        </div>
      </div>
    );
  }

  const panel = elegido ? (
    <PanelHoras
      key={elegido}
      fecha={elegido}
      libres={disp.libres(elegido)}
      listo={disp.tiene(mesDe(elegido))}
      casillas={Array.from({ length: config.horaCierre - config.horaApertura }, (_, i) => config.horaApertura + i)}
      tarifa={config.tarifaPorHora}
      recargar={disp.recargar}
      previa={previa}
      soltando={soltando}
    />
  ) : (
    <div className="contenido">
      <p className="parrafo">Elige un día en el calendario para ver sus horas.</p>
    </div>
  );

  // Celular, paso 2: solo las horas.
  if (!escritorio) {
    return (
      <div className="pantalla">
        <CabeceraPaso volverA="/" volverTexto="Volver a elegir día" antetitulo="Paso 2 de 3" titulo={elegido ? capital(fechaLarga(elegido)) : ''} />
        {panel}
      </div>
    );
  }

  // Escritorio: las dos columnas.
  return (
    <div className="pantalla pantalla--ancha">
      <CabeceraMarca accion={{ a: '/acceso', texto: '¿Ya reservaste? Busca tu sesión' }} />
      <div className="reservar">
        {calendario}
        <div className="reservar__panel">
          {elegido && (
            <div style={{ marginBottom: 8 }}>
              <div className="antetitulo">Elegido</div>
              <h2 className="titulo titulo--sub">{capital(fechaLarga(elegido))}</h2>
            </div>
          )}
          {panel}
        </div>
      </div>
    </div>
  );
}

const capital = (s: string) => s[0]!.toUpperCase() + s.slice(1);

function PanelHoras({
  fecha,
  libres,
  listo,
  casillas,
  tarifa,
  recargar,
  previa,
  soltando,
}: {
  fecha: Fecha;
  libres: Hora[];
  listo: boolean;
  casillas: Hora[];
  tarifa: number;
  recargar: () => void;
  previa: Retencion | undefined;
  soltando: boolean;
}) {
  const navegar = useNavigate();
  // Si viene de vuelta desde el pago, recupera lo que había elegido.
  const [elegidas, setElegidas] = useState<Hora[]>(() => (previa?.fecha === fecha ? previa.horas : []));
  const [tema, setTema] = useState(() => previa?.tema ?? '');
  const [correo, setCorreo] = useState(() => leerLocal(CLAVE_CORREO));
  const [correoTocado, setCorreoTocado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const refError = useRef<HTMLDivElement>(null);

  const libresSet = useMemo(() => new Set(libres), [libres]);
  const ocupadasHoy = casillas.filter((h) => !libresSet.has(h));
  const total = elegidas.length * tarifa;
  const correoOk = CORREO_VALIDO.test(correo.trim());

  // Si al recargar alguna elegida dejó de estar libre, se quita sola.
  useEffect(() => {
    if (listo && !soltando) setElegidas((e) => e.filter((h) => libresSet.has(h)));
  }, [listo, libresSet, soltando]);

  const alternar = (h: Hora) =>
    setElegidas((e) => (e.includes(h) ? e.filter((x) => x !== h) : [...e, h].sort((a, b) => a - b)));

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setCorreoTocado(true);
    if (elegidas.length === 0 || !correoOk || enviando) return;
    setEnviando(true);
    setError(undefined);
    try {
      const r = await api.reservar({ fecha, horas: elegidas, correo: correo.trim(), ...(tema.trim() ? { tema: tema.trim() } : {}) });
      recordarLocal(CLAVE_CORREO, correo.trim());
      recordarCorreo(r.id, correo.trim().toLowerCase());
      recordarRetencion({ id: r.id, fecha, horas: elegidas, ...(tema.trim() ? { tema: tema.trim() } : {}) });
      navegar(`/pago/${r.id}`);
    } catch (e) {
      setEnviando(false);
      if (e instanceof ErrorDeApi && (e.motivo === 'HORAS_OCUPADAS' || e.motivo === 'NO_RESERVABLE')) recargar();
      setError(mensajeDeError(e));
      requestAnimationFrame(() => refError.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  }

  if (!listo || soltando) return <Cargando texto={soltando ? 'Liberando tus horas para que las cambies…' : 'Buscando horas libres…'} />;

  return (
    <form onSubmit={enviar} noValidate style={{ display: 'contents' }}>
      <div className="contenido">
        {libres.length === 0 && (
          <AvisoError>
            Este día ya no tiene horas libres. <Link to="/">Elige otro día</Link>.
          </AvisoError>
        )}

        <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
          <div className="fila">
            <legend className="campo__etiqueta" style={{ padding: 0 }}>
              ¿Qué horas necesitas?
            </legend>
            <span className="nota">{soles(tarifa)} cada una</span>
          </div>
          <GrillaHoras casillas={casillas} libres={libresSet} elegidas={elegidas} onAlternar={alternar} />
          <p className="nota">
            Horas de Lima. Toca las que quieras, seguidas o no.
            {ocupadasHoy.length > 0 && ocupadasHoy.length < casillas.length && ' Las tachadas ya no están disponibles.'}
          </p>
        </fieldset>

        <div className="campo">
          <label htmlFor="tema" className="campo__etiqueta">
            ¿Qué tienes atorado?
          </label>
          <textarea
            id="tema"
            className="campo__entrada"
            rows={3}
            maxLength={1000}
            value={tema}
            onChange={(e) => setTema(e.target.value)}
            placeholder="La decisión que vienes postergando, el problema que das vueltas hace semanas. En dos o tres líneas."
            aria-describedby="tema-ayuda"
          />
          <span id="tema-ayuda" className="campo__ayuda">
            Opcional, pero si lo llenas llego preparado y no gastamos la primera media hora en contexto.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="correo" className="campo__etiqueta">
            Tu correo
          </label>
          <input
            id="correo"
            className="campo__entrada"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tu@correo.com"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            onBlur={() => setCorreoTocado(true)}
            aria-invalid={correoTocado && !correoOk}
            aria-describedby="correo-ayuda"
            required
          />
          <span id="correo-ayuda" className={correoTocado && !correoOk ? 'campo__error' : 'campo__ayuda'}>
            {correoTocado && !correoOk ? 'Revisa tu correo: ahí te llega el enlace de tu sesión.' : 'Ahí te llega el enlace de tu sesión. No necesitas crear cuenta.'}
          </span>
        </div>

        <div className="tarjeta" aria-live="polite">
          <div className="antetitulo">Tu reserva</div>
          {elegidas.length > 0 ? (
            <div className="chips">
              {elegidas.map((h) => (
                <span key={h} className="chip">
                  {hhmm(h)} – {hhmm(h + 1)}
                </span>
              ))}
            </div>
          ) : (
            <span className="nota">Todavía no eliges ninguna hora.</span>
          )}
          <hr className="separador" />
          <div className="fila">
            <span className="parrafo">
              {elegidas.length === 0 ? 'Sin horas elegidas' : `${elegidas.length} ${elegidas.length === 1 ? 'hora' : 'horas'} × ${soles(tarifa)}`}
            </span>
            <span className="monto" style={{ fontSize: 26 }}>
              {soles(total)}
            </span>
          </div>
        </div>

        {error && (
          <div ref={refError}>
            <AvisoError>{error}</AvisoError>
          </div>
        )}

        <div className="aviso">
          <Escudo />
          <span>Al continuar, tus horas quedan separadas 15 minutos mientras pagas.</span>
        </div>
      </div>

      <div className="pie">
        <button type="submit" className="boton" disabled={elegidas.length === 0 || enviando} aria-disabled={!correoOk}>
          {enviando ? 'Separando tus horas…' : elegidas.length ? `Ir a pagar · ${soles(total)}` : 'Elige al menos una hora'}
          {!enviando && elegidas.length > 0 && <FlechaDer color="currentColor" />}
        </button>
      </div>
    </form>
  );
}
